/**
 * useNodeOperations - Hook for node CRUD operations
 *
 * Handles:
 * - Delete node (with validation)
 * - Collapse/expand node hierarchies
 * - Update node label and fields
 * - Add child node (directly creates based on parent category)
 */

import { useCallback } from 'react'
import type { Node, Edge } from '@xyflow/react'
import type { MindMapNode, NodeCategory, ProjectLabel } from '@bandos/shared'
import type { ReactFlowNodeData, SummarizedNodeData } from '../../../../types/mindmap-components'
import { updateNodeField } from '../../../../utils'
import { getTargetInfo } from '../../Menus/CategoryOptionsList.utils'
import {
  buildCollapsedHierarchy,
  getDescendantNodeIds,
  getEdgesToHide,
} from '../../../../utils/collapseUtils'
import { AIService } from '../../../../services/ai/aiService'

interface UseNodeOperationsConfig {
  setNodes: React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  getCurrentNodes: () => Node<ReactFlowNodeData>[]
  getNodeLabelsRef: React.RefObject<((nodeId: string) => ProjectLabel[]) | null>
  executeChildNodeCreation: (
    parentNodeId: string,
    childCategory: NodeCategory
  ) => string | undefined
  /** Called when a node's label is updated - used to dismiss AI suggestions */
  onNodeLabelChanged?: (nodeId: string) => void
  /** Called when a node is deleted - used to dismiss AI suggestions */
  onNodeDeleted?: (nodeId: string) => void
  /** Called when a venture node is deleted - used to sync with backend */
  onVentureNodeDeleted?: () => void
}

interface UseNodeOperationsReturn {
  handleAddChild: (nodeId: string, nodeCategory: NodeCategory) => void
  handleDeleteNode: (nodeId: string) => void
  handleCollapseNode: (nodeId: string) => void
  handleExpandNode: (nodeId: string) => void
  handleUpdateNodeLabel: (nodeId: string, newLabel: string) => void
  handleUpdateNode: (nodeId: string, updates: Partial<MindMapNode>) => void
}

export function useNodeOperations({
  setNodes,
  setEdges,
  getCurrentNodes,
  getNodeLabelsRef,
  executeChildNodeCreation,
  onNodeLabelChanged,
  onNodeDeleted,
  onVentureNodeDeleted,
}: UseNodeOperationsConfig): UseNodeOperationsReturn {
  /**
   * Creates a child node directly based on the parent's category.
   * Maps: venture→need, need→idea, idea→idea.
   */
  const handleAddChild = useCallback(
    (nodeId: string, nodeCategory: NodeCategory) => {
      const targetInfo = getTargetInfo(nodeCategory)
      if (!targetInfo) return
      executeChildNodeCreation(nodeId, targetInfo.targetCategory)
    },
    [executeChildNodeCreation]
  )

  /**
   * Deletes a node from the mindmap.
   * Also removes all edges connected to the deleted node.
   * Notifies parent when a venture node is deleted (to sync with backend).
   */
  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      // Use functional updates to avoid stale closure issues
      setNodes((currentNodes) => {
        // Find the node to check its category
        const nodeToDelete = currentNodes.find((n) => n.id === nodeId)

        // If deleting a venture node, notify parent to sync with backend
        if (nodeToDelete && nodeToDelete.data.category === 'venture') {
          onVentureNodeDeleted?.()
        }

        // Clean up storage images for nodes that have them
        if (nodeToDelete) {
          const urls: string[] = []
          if (nodeToDelete.data.storyboardUrls) {
            urls.push(...nodeToDelete.data.storyboardUrls.filter(Boolean))
          }
          if (urls.length > 0) {
            void AIService.deleteImages(urls)
          }
        }

        // Remove node
        return currentNodes.filter((n) => n.id !== nodeId)
      })

      // Remove all edges connected to the deleted node
      setEdges((currentEdges) =>
        currentEdges.filter((e) => e.source !== nodeId && e.target !== nodeId)
      )

      // Notify parent that node was deleted (used for AI suggestion dismissal)
      onNodeDeleted?.(nodeId)
    },
    [setNodes, setEdges, onVentureNodeDeleted, onNodeDeleted]
  )

  /**
   * Expands a collapsed node back to its original state.
   * Restores the original node type and unhides all descendants.
   * Applies position offset if the collapsed node was moved.
   */
  const handleExpandNode = useCallback(
    (nodeId: string) => {
      // Use functional update to get the latest node state
      setNodes((prevNodes) => {
        const node = prevNodes.find((n: Node<ReactFlowNodeData>) => n.id === nodeId)
        if (!node || node.type !== 'summarizedNode') {
          return prevNodes
        }

        const summarizedData = node.data as SummarizedNodeData

        // Get all descendant IDs from the stored data
        const descendantIds = summarizedData.hiddenDescendantIds
        const hiddenEdgeIds = summarizedData.hiddenEdgeIds

        // Calculate position offset (how much the collapsed node was moved)
        const originalPosition = summarizedData.originalPosition
        const offsetX = node.position.x - originalPosition.x
        const offsetY = node.position.y - originalPosition.y

        // Create a Set for faster lookup
        const descendantIdSet = new Set(descendantIds)
        const hiddenEdgeIdSet = new Set(hiddenEdgeIds)

        // Unhide edges (inside setNodes to ensure we have the data)
        setEdges((currentEdges) =>
          currentEdges.map((e) => {
            if (hiddenEdgeIdSet.has(e.id)) {
              return { ...e, hidden: false }
            }
            return e
          })
        )

        // Restore nodes with position offset applied to descendants
        return prevNodes.map((n) => {
          if (n.id === nodeId) {
            // Restore root to original type (keep current position)
            return {
              ...n,
              type: summarizedData.originalNodeType,
              data: summarizedData.originalNodeData,
            }
          }
          if (descendantIdSet.has(n.id)) {
            // Unhide descendant nodes and apply position offset
            return {
              ...n,
              hidden: false,
              position: {
                x: n.position.x + offsetX,
                y: n.position.y + offsetY,
              },
            }
          }
          return n
        })
      })
    },
    [setNodes, setEdges]
  )

  /**
   * Collapses a node and all its descendants into a summarized view.
   * Transforms the root node to a SummarizedNode and hides all descendants.
   */
  const handleCollapseNode = useCallback(
    (nodeId: string) => {
      // Get current state from ref to compute the hierarchy
      const currentNodes = getCurrentNodes()
      const node = currentNodes.find((n: Node<ReactFlowNodeData>) => n.id === nodeId)
      if (!node) return

      // Don't collapse if it's already a summarized node
      if (node.type === 'summarizedNode') return

      // First, update edges and compute necessary data
      setEdges((currentEdges) => {
        // Pass getNodeLabels from context ref to capture labels from LabelContext
        const hierarchy = buildCollapsedHierarchy(
          nodeId,
          currentNodes,
          currentEdges,
          getNodeLabelsRef.current ?? undefined
        )

        // Get all descendants to hide
        const allDescendantIds = getDescendantNodeIds(nodeId, currentEdges)

        // Only track descendants that are currently VISIBLE
        const descendantIds = allDescendantIds.filter((id) => {
          const descendantNode = currentNodes.find((n) => n.id === id)
          return descendantNode && !descendantNode.hidden
        })

        // Get edges to hide
        const edgesToHide = getEdgesToHide(nodeId, descendantIds, currentEdges).filter((edgeId) => {
          const edge = currentEdges.find((e) => e.id === edgeId)
          return edge && !edge.hidden
        })

        // Create sets for faster lookup
        const descendantIdSet = new Set(descendantIds)
        const edgesToHideSet = new Set(edgesToHide)

        // Update nodes synchronously (React batches these updates)
        setNodes((prevNodes) => {
          return prevNodes.map((n) => {
            if (n.id === nodeId) {
              // Transform root to summarized node
              const summarizedData: SummarizedNodeData = {
                ...n.data,
                category: 'summarized' as const,
                collapsedHierarchy: hierarchy,
                originalNodeType: n.type ?? 'ideaNode',
                originalNodeData: { ...n.data },
                originalPosition: { x: n.position.x, y: n.position.y },
                hiddenDescendantIds: descendantIds,
                hiddenEdgeIds: edgesToHide,
                onExpandNode: handleExpandNode,
              }
              return {
                ...n,
                type: 'summarizedNode',
                data: summarizedData,
              }
            }
            if (descendantIdSet.has(n.id)) {
              // Hide descendant nodes
              return { ...n, hidden: true }
            }
            return n
          })
        })

        // Hide affected edges
        return currentEdges.map((e) => {
          if (edgesToHideSet.has(e.id)) {
            return { ...e, hidden: true }
          }
          return e
        })
      })
    },
    [setNodes, setEdges, getCurrentNodes, getNodeLabelsRef, handleExpandNode]
  )

  /**
   * Updates the label (main text content) of a node.
   * Used by all node types for their primary text field.
   * Also notifies parent to dismiss any AI suggestions for this node since the context has changed.
   */
  const handleUpdateNodeLabel = useCallback(
    (nodeId: string, newLabel: string) => {
      setNodes((nodes) => updateNodeField(nodes, nodeId, 'label', newLabel, undefined, true))
      // Notify parent that label changed (used for AI suggestion dismissal)
      onNodeLabelChanged?.(nodeId)
    },
    [setNodes, onNodeLabelChanged]
  )

  /**
   * Updates a node with partial data (allows updating multiple fields at once).
   * Used primarily by AI operations like generate_figma_prototype that need to update
   * node properties or create new nodes.
   */
  const handleUpdateNode = useCallback(
    (nodeId: string, updates: Partial<MindMapNode>) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              ...updates,
              data: {
                ...node.data,
                ...(updates.data ?? {}),
              },
            }
          }
          return node
        })
      )
    },
    [setNodes]
  )

  return {
    handleAddChild,
    handleDeleteNode,
    handleCollapseNode,
    handleExpandNode,
    handleUpdateNodeLabel,
    handleUpdateNode,
  }
}
